import React, { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

const stripePromiseCache = {};
export function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (!stripePromiseCache[publishableKey]) {
    stripePromiseCache[publishableKey] = loadStripe(publishableKey);
  }
  return stripePromiseCache[publishableKey];
}

const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#4f46e5',
    colorBackground: '#ffffff',
    colorText: '#111827',
    colorDanger: '#dc2626',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSizeBase: '14px',
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      padding: '10px 14px',
      transition: 'box-shadow 150ms, border-color 150ms',
    },
    '.Input:hover': {
      borderColor: '#d1d5db',
    },
    '.Input:focus': {
      borderColor: '#4f46e5',
      boxShadow: '0 0 0 2px rgba(79, 70, 229, 0.2)',
    },
    '.Label': {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '6px',
    },
    '.Tab': {
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
    },
    '.Tab:hover': {
      borderColor: '#c7d2fe',
    },
    '.Tab--selected': {
      borderColor: '#4f46e5',
      boxShadow: '0 0 0 1px #4f46e5',
    },
  },
};

const primaryBtnClass = "w-full rounded-lg bg-indigo-600 px-5 py-3 text-sm/6 font-semibold text-white shadow-sm ring-1 ring-inset ring-indigo-600/20 transition duration-150 hover:bg-indigo-700 hover:shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none";

function PaymentInner({ returnUrl, submitLabel, intent }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const confirmFn = intent === 'setup' ? stripe.confirmSetup : stripe.confirmPayment;
    const { error: stripeError } = await confirmFn.call(stripe, {
      elements,
      confirmParams: { return_url: returnUrl },
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed. Please try again.');
      setSubmitting(false);
    }
  };

  const defaultLabel = intent === 'setup' ? 'Save card' : 'Pay now';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
      )}

      <PaymentElement options={{ layout: { type: 'tabs', defaultCollapsed: false } }} />

      <button type="submit" disabled={!stripe || submitting} className={primaryBtnClass}>
        {submitting ? 'Processing…' : (submitLabel || defaultLabel)}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your card is processed securely by Stripe. We never see or store your card number.
      </p>
    </form>
  );
}

export function FreeOrderConfirm({ returnUrl, reason = 'promo' }) {
  const [submitting, setSubmitting] = useState(false);
  const source = reason === 'deal' ? 'This offer brings' : 'Your promo brings';
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        {source} the total to $0, so there is nothing to pay today.
      </div>
      <button
        type="button"
        disabled={submitting}
        onClick={() => { setSubmitting(true); window.location.assign(returnUrl); }}
        className={primaryBtnClass}
      >
        {submitting ? 'Redirecting…' : 'Confirm and continue'}
      </button>
    </div>
  );
}

export default function PaymentForm({ clientSecret, publishableKey, returnUrl, submitLabel, intent = 'payment' }) {
  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  if (!clientSecret || !stripePromise) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
        Loading payment form…
      </div>
    );
  }

  // `key={clientSecret}` is load-bearing: Stripe's <Elements> reads clientSecret
  // ONCE at mount and ignores later prop changes. Applying/removing a promo code
  // re-prepares a NEW PaymentIntent (new clientSecret + new return_url) via
  // router.reload(); without a key, React re-renders the same <Elements> instance
  // and confirmPayment charges the OLD intent (full price) while return_url points
  // at the new pending_purchase — the buyer is overcharged AND lands on a page that
  // can't verify the (never-paid) new intent. Keying on clientSecret forces a
  // remount so the mounted intent always matches the displayed total.
  return (
    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance }}>
      <PaymentInner returnUrl={returnUrl} submitLabel={submitLabel} intent={intent} />
    </Elements>
  );
}
